import { useEffect, useState } from "react";

export interface FloatingPanelPosition {
  /** Top-Koordinate in Viewport-Pixeln (für `position: fixed`). */
  top: number;
  /** Left-Koordinate in Viewport-Pixeln. */
  left: number;
  /** Empfohlene Breite – entspricht der Triggerbreite. */
  width: number;
  /** Maximale verfügbare Höhe nach unten/oben (für `max-height`). */
  maxHeight: number;
  /** True, wenn das Panel oberhalb des Triggers gerendert werden soll. */
  flipped: boolean;
}

interface UseFloatingPanelOptions {
  /** Vertikaler Abstand zwischen Trigger und Panel in Pixeln (Default 4). */
  gap?: number;
  /**
   * Mindestens nötiger Platz nach unten, damit das Panel unten bleibt.
   * Wird er unterschritten und oberhalb des Triggers ist mehr Platz, wird
   * geflippt (Default 160).
   */
  minBelow?: number;
  /** Hard-Cap für die maximale Höhe (Default 288 = `max-h-72`). */
  maxHeightCap?: number;
}

/**
 * Berechnet eine viewport-fixierte Position für ein Dropdown-Panel relativ
 * zu einem Trigger-Element.
 */
export function useFloatingPanel(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  options: UseFloatingPanelOptions = {},
): FloatingPanelPosition | null {
  const { gap = 4, minBelow = 160, maxHeightCap = 288 } = options;
  const [pos, setPos] = useState<FloatingPanelPosition | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    function update() {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const spaceBelow = Math.max(0, viewportH - rect.bottom - gap - 8);
      const spaceAbove = Math.max(0, rect.top - gap - 8);

      const flipped = spaceBelow < minBelow && spaceAbove > spaceBelow;

      const available = Math.min(flipped ? spaceAbove : spaceBelow, maxHeightCap);

      const top = flipped ? rect.top - gap - available : rect.bottom + gap;
      setPos({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: available,
        flipped,
      });
    }

    update();
    const raf = window.requestAnimationFrame(update);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, triggerRef, gap, minBelow, maxHeightCap]);

  return pos;
}
